import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiCreditCard, FiDownload, FiPrinter, FiRotateCcw,
  FiDollarSign, FiArrowUp, FiArrowDown, FiHash
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { expandMultiPartyTransactions } from '../../utils/multiPartyHelper';
import { paymentNet } from '../../utils/receiptDeductionsHelper';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];
const STATUS_OPTIONS = ['All', 'Draft', 'Posted', 'Cancelled'];
const PAYMENT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Payments' },
  { value: 'CP', label: 'Cash Payment (CP)' },
  { value: 'BP', label: 'Bank Payment (BP)' }
];

const STATUS_COLORS = {
  Draft: { bg: `${colors.neutral[500]}18`, text: colors.neutral[600] },
  Posted: { bg: `${colors.success.main}15`, text: colors.success.main },
  Cancelled: { bg: `${colors.error.main}15`, text: colors.error.main }
};

const PAYMENT_TYPE_LABELS = {
  CP: 'Cash Payment',
  BP: 'Bank Payment'
};

// ── Helpers ──

const getWeekNumber = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return Math.round(((date - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7) + 1;
};

const getPeriodKey = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth();
  return `${year}-${String(month + 1).padStart(2, '0')}`;
};

const getPeriodLabel = (key) => {
  if (!key || key === 'Unknown') return 'Unknown';
  const [y, m] = key.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

// ── Component ──

const VendorPayments = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + loaded rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('vendor-payments');
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';

  // Data states
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [vendors, setVendors] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(() => savedMem ? false : true);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedVendor, setSelectedVendor] = useState(() => savedMem?.selectedVendor ?? 'all');
  const [paymentType, setPaymentType] = useState(() => savedMem?.paymentType ?? 'all');
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'All');

  // Sort states
  const [sortColumn, setSortColumn] = useState(() => savedMem?.sortColumn ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // Fresh visit only — a restored snapshot keeps the user's chosen year.
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  // ── Load Vendors ──
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(ref, orderBy('vendorName'));
      const snapshot = await getDocs(q);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // ── Load Payment Transactions ──
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    try {
      setLoading(true);
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');

      const conditions = [
        where('tranTypeCode', 'in', ['CP', 'BP']),
        where('financialYearId', '==', selectedFYId)
      ];

      const q = query(ref, ...conditions);
      const snapshot = await getDocs(q);
      let data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Expand multi-party payments into per-party rows
      data = expandMultiPartyTransactions(data);
      // Net paid is DERIVED (gross − WHT − adjustment): the stored doc-level
      // netAmount is overwritten with the gross by the save code, which made this
      // report show WHT payments at their gross amount.
      data = data.map(t => ({ ...t, netAmount: paymentNet(t) }));

      // Sort by docDate descending by default
      data.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setTransactions(data);
    } catch (error) {
      console.error('Error loading payment transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);
  useEffect(() => { loadVendors(); }, [loadVendors]);
  // On Back (restored), SKIP ONLY the first auto-load — restored rows already hold
  // the data (0 re-reads) — but still let a later Financial-Year change re-fetch.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    if (selectedFYId) loadTransactions();
  }, [loadTransactions, selectedFYId]);

  // Remember this view (filters + loaded rows) so Back restores it with 0 re-reads.
  // Filters here are display-only over already-loaded data, so we keep the snapshot
  // in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (loading) return;
    saveMemory({
      transactions,
      selectedFYId, dateFrom, dateTo, selectedVendor, paymentType, statusFilter,
      sortColumn, sortDirection
    });
  }, [loading, transactions, selectedFYId, dateFrom, dateTo, selectedVendor,
      paymentType, statusFilter, sortColumn, sortDirection, saveMemory]);

  // ── Filtered Data ──
  const filteredData = useMemo(() => {
    let data = [...transactions];

    if (dateFrom) {
      data = data.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      data = data.filter(t => (t.docDate || '') <= dateTo);
    }
    if (selectedVendor !== 'all') {
      data = data.filter(t => t.partyId === selectedVendor);
    }
    if (paymentType !== 'all') {
      data = data.filter(t => t.tranTypeCode === paymentType);
    }
    if (statusFilter !== 'All') {
      data = data.filter(t => t.status === statusFilter);
    }

    return data;
  }, [transactions, dateFrom, dateTo, selectedVendor, paymentType, statusFilter]);

  // ── Sorted Data ──
  const sortedData = useMemo(() => {
    const data = [...filteredData];
    data.sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'docDate':
          valA = a.docDate || '';
          valB = b.docDate || '';
          break;
        case 'docNumber':
          valA = a.docNumber || '';
          valB = b.docNumber || '';
          break;
        case 'tranTypeCode':
          valA = a.tranTypeCode || '';
          valB = b.tranTypeCode || '';
          break;
        case 'partyName':
          valA = (a.partyName || '').toLowerCase();
          valB = (b.partyName || '').toLowerCase();
          break;
        case 'totalAmount':
          valA = a.totalAmount || 0;
          valB = b.totalAmount || 0;
          break;
        case 'netAmount':
          valA = a.netAmount || 0;
          valB = b.netAmount || 0;
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        default:
          valA = a.docDate || '';
          valB = b.docDate || '';
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredData, sortColumn, sortDirection]);

  // ── Summary Calculations ──
  const summary = useMemo(() => {
    const cpTransactions = filteredData.filter(t => t.tranTypeCode === 'CP');
    const bpTransactions = filteredData.filter(t => t.tranTypeCode === 'BP');

    const totalPaid = filteredData.reduce((sum, t) => sum + (t.netAmount || 0), 0);
    const cashPayments = cpTransactions.reduce((sum, t) => sum + (t.netAmount || 0), 0);
    const bankPayments = bpTransactions.reduce((sum, t) => sum + (t.netAmount || 0), 0);

    return {
      totalPaid,
      cashPayments,
      bankPayments,
      paymentCount: filteredData.length,
      cpCount: cpTransactions.length,
      bpCount: bpTransactions.length
    };
  }, [filteredData]);

  // ── Chart Data: Payment Trend (Area) ──
  const trendChartData = useMemo(() => {
    const periodMap = {};

    filteredData.forEach(t => {
      const key = getPeriodKey(t.docDate);
      if (!periodMap[key]) {
        periodMap[key] = { period: key, cash: 0, bank: 0, total: 0 };
      }
      const amount = t.netAmount || 0;
      if (t.tranTypeCode === 'CP') {
        periodMap[key].cash += amount;
      } else if (t.tranTypeCode === 'BP') {
        periodMap[key].bank += amount;
      }
    });

    const sorted = Object.values(periodMap).sort((a, b) => a.period.localeCompare(b.period));
    sorted.forEach(item => {
      item.total = item.cash + item.bank;
      item.label = getPeriodLabel(item.period);
    });

    return sorted;
  }, [filteredData]);

  // ── Chart Data: Cash vs Bank (Pie) ──
  const paymentModePieData = useMemo(() => {
    const data = [];
    if (summary.cashPayments > 0) {
      data.push({ name: 'Cash Payments (CP)', value: summary.cashPayments });
    }
    if (summary.bankPayments > 0) {
      data.push({ name: 'Bank Payments (BP)', value: summary.bankPayments });
    }
    return data;
  }, [summary]);

  // ── Chart Data: Top 10 Vendors by Payments (Bar) ──
  const vendorBarChartData = useMemo(() => {
    const vendorMap = {};
    filteredData.forEach(t => {
      const name = t.partyName || 'Unknown';
      if (!vendorMap[name]) {
        vendorMap[name] = { vendor: name, amount: 0 };
      }
      vendorMap[name].amount += t.netAmount || 0;
    });

    return Object.values(vendorMap)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10)
      .map(item => ({
        ...item,
        vendor: item.vendor.length > 18 ? item.vendor.substring(0, 18) + '...' : item.vendor
      }));
  }, [filteredData]);

  // ── Sort Handler ──
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <FiArrowUp size={11} style={{ marginLeft: 4 }} />
      : <FiArrowDown size={11} style={{ marginLeft: 4 }} />;
  };

  // ── Navigation ──
  const handleDocClick = (transaction) => {
    const route = transaction.tranTypeCode === 'CP'
      ? '/payments/cash-payment'
      : '/payments/bank-payment';
    navigate(route, { state: { viewTransactionId: transaction.id } });
  };

  // ── Reset Filters ──
  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedVendor('all');
    setPaymentType('all');
    setStatusFilter('All');
    if (activeFinancialYear?.id) {
      setSelectedFYId(activeFinancialYear.id);
    }
  };

  // ── CSV Export ──
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Vendor Payments Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'}\n`;
    csv += `Payment Type: ${paymentType === 'all' ? 'All' : paymentType}\n`;
    csv += `Status: ${statusFilter}\n\n`;

    csv += 'Date,Doc #,Type,Vendor,Amount,Payment Mode,Status\n';

    sortedData.forEach(t => {
      csv += `"${formatDate(t.docDate)}",`;
      csv += `"${t.docNumber || ''}",`;
      csv += `"${t.tranTypeCode || ''}",`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}",`;
      csv += `${t.netAmount || 0},`;
      csv += `"${PAYMENT_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || ''}",`;
      csv += `"${t.status || ''}"\n`;
    });

    csv += `\nTotal Paid:,${summary.totalPaid}\n`;
    csv += `Cash Payments (CP):,${summary.cashPayments}\n`;
    csv += `Bank Payments (BP):,${summary.bankPayments}\n`;
    csv += `Payment Count:,${summary.paymentCount}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Vendor_Payments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Vendor Payments Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary-row{display:flex;gap:20px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{border:1px solid #ddd;border-radius:6px;padding:10px 16px;min-width:140px}
        .summary-box .label{font-size:11px;color:#666;text-transform:uppercase}
        .summary-box .value{font-size:18px;font-weight:600;color:#1A1A1A}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th{background:#800020;color:white;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:700}
        .totals-row td{border-top:2px solid #800020;padding:10px}
        .status{display:inline-block;padding:2px 8px;border-radius:3px;font-size:10px;font-weight:600}
        .status-Posted{background:#28A74515;color:#28A745}
        .status-Draft{background:#ADB5BD18;color:#6C757D}
        .status-Cancelled{background:#DC354515;color:#DC3545}
        .type-badge{display:inline-block;padding:2px 6px;border-radius:3px;font-size:10px;font-weight:600}
        .type-CP{background:#80002012;color:#800020}
        .type-BP{background:#17A2B815;color:#17A2B8}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Vendor Payments Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'} |
        Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'} |
        Payment Type: ${paymentType === 'all' ? 'All' : paymentType} |
        Status: ${statusFilter} |
        Total Records: ${sortedData.length}
      </div>
      <div class="summary-row">
        <div class="summary-box"><div class="label">Total Paid</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalPaid)}</div></div>
        <div class="summary-box"><div class="label">Cash Payments (CP)</div><div class="value">${currencySymbol}${formatAmountLocal(summary.cashPayments)}</div></div>
        <div class="summary-box"><div class="label">Bank Payments (BP)</div><div class="value">${currencySymbol}${formatAmountLocal(summary.bankPayments)}</div></div>
        <div class="summary-box"><div class="label">Payment Count</div><div class="value">${formatNumber(summary.paymentCount)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>Doc #</th><th>Type</th><th>Vendor</th>
          <th class="right">Amount</th><th>Payment Mode</th><th>Status</th>
        </tr></thead>
        <tbody>`);

    sortedData.forEach(t => {
      w.document.write(`
        <tr>
          <td>${formatDate(t.docDate)}</td>
          <td>${t.docNumber || ''}</td>
          <td><span class="type-badge type-${t.tranTypeCode}">${t.tranTypeCode || ''}</span></td>
          <td>${t.partyName || ''}</td>
          <td class="right">${formatAmountLocal(t.netAmount)}</td>
          <td>${PAYMENT_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || ''}</td>
          <td><span class="status status-${t.status || 'Draft'}">${t.status || 'Draft'}</span></td>
        </tr>`);
    });

    // Totals row
    const totNet = sortedData.reduce((s, t) => s + (t.netAmount || 0), 0);

    w.document.write(`
        <tr class="totals-row">
          <td colspan="4" style="text-align:right">TOTAL (${sortedData.length} records)</td>
          <td class="right">${formatAmountLocal(totNet)}</td>
          <td></td>
          <td></td>
        </tr>
        </tbody></table>
        <div style="margin-top:30px;text-align:center;color:#999;font-size:11px">
          Generated on ${new Date().toLocaleString()} | AMAL ERP
        </div>
      </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Totals Row for Table ──
  const tableTotals = useMemo(() => {
    return {
      netAmount: sortedData.reduce((s, t) => s + (t.netAmount || 0), 0)
    };
  }, [sortedData]);

  // ── Custom Tooltip for Charts ──
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{label}</ChartTooltipLabel>
        {payload.map((entry, i) => (
          <ChartTooltipRow key={i}>
            <ChartTooltipDot style={{ background: entry.color }} />
            <span>{entry.name}:</span>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        ))}
      </ChartTooltipWrapper>
    );
  };

  const PieCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    const total = paymentModePieData.reduce((s, d) => s + d.value, 0);
    const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{data.name}</ChartTooltipLabel>
        <ChartTooltipRow>
          <span>Amount:</span>
          <strong>{formatCurrency(data.value)}</strong>
        </ChartTooltipRow>
        <ChartTooltipRow>
          <span>Share:</span>
          <strong>{pct}%</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  // ── Render ──
  return (
    <PageLayout pageTitle="Vendor Payments" breadcrumb="Reports / Purchase Reports / Vendor Payments">
      {/* Summary Cards */}
      <CardsGrid>
        <SummaryCard>
          <CardIcon color={colors.primary.main}>
            <FiDollarSign />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Paid</CardLabel>
            <CardValue>{formatCurrency(summary.totalPaid)}</CardValue>
            <CardSub>{summary.paymentCount} payment{summary.paymentCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color="#8B7355">
            <FiCreditCard />
          </CardIcon>
          <CardInfo>
            <CardLabel>Cash Payments (CP)</CardLabel>
            <CardValue>{formatCurrency(summary.cashPayments)}</CardValue>
            <CardSub>{summary.cpCount} transaction{summary.cpCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.info.main}>
            <FiCreditCard />
          </CardIcon>
          <CardInfo>
            <CardLabel>Bank Payments (BP)</CardLabel>
            <CardValue>{formatCurrency(summary.bankPayments)}</CardValue>
            <CardSub>{summary.bpCount} transaction{summary.bpCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.warning.main}>
            <FiHash />
          </CardIcon>
          <CardInfo>
            <CardLabel>Payment Count</CardLabel>
            <CardValue>{formatNumber(summary.paymentCount)}</CardValue>
            <CardSub>CP: {summary.cpCount} | BP: {summary.bpCount}</CardSub>
          </CardInfo>
        </SummaryCard>
      </CardsGrid>

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select
              value={selectedFYId}
              onChange={(e) => setSelectedFYId(e.target.value)}
            >
              <option value="">-- Select Year --</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <DateInput
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <DateInput
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={selectedVendor}
              onChange={setSelectedVendor}
              allOption={{ value: 'all', label: 'All Vendors' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Payment Type</Label>
            <Select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
            >
              {PAYMENT_TYPE_OPTIONS.map(pt => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FilterGroup>

          <ButtonGroup>
            <Button onClick={handleResetFilters} title="Reset Filters">
              <FiRotateCcw /> Reset
            </Button>
            <Button onClick={handleExport}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint}>
              <FiPrinter /> Print
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Data Table */}
      <ContentCard>
        <TableHeader>
          <TableHeaderTitle>
            Vendor Payment Transactions
            <TableCount>{sortedData.length} record{sortedData.length !== 1 ? 's' : ''}</TableCount>
          </TableHeaderTitle>
        </TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <EmptyText>Loading payment data...</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiCreditCard /></EmptyIcon>
              <EmptyText>
                {transactions.length === 0
                  ? 'No payment transactions found for this financial year'
                  : 'No transactions match the selected filters'}
              </EmptyText>
              <EmptySubText>Try adjusting your filters or selecting a different financial year</EmptySubText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th sortable onClick={() => handleSort('docDate')}>
                    Date {renderSortIcon('docDate')}
                  </Th>
                  <Th sortable onClick={() => handleSort('docNumber')}>
                    Doc # {renderSortIcon('docNumber')}
                  </Th>
                  <Th sortable onClick={() => handleSort('tranTypeCode')}>
                    Type {renderSortIcon('tranTypeCode')}
                  </Th>
                  <Th sortable onClick={() => handleSort('partyName')}>
                    Vendor {renderSortIcon('partyName')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('netAmount')}>
                    Amount {renderSortIcon('netAmount')}
                  </Th>
                  <Th>Payment Mode</Th>
                  <Th align="center" sortable onClick={() => handleSort('status')}>
                    Status {renderSortIcon('status')}
                  </Th>
                </tr>
              </Thead>
              <Tbody>
                {sortedData.map(t => (
                  <Tr key={t.id}>
                    <Td>{formatDate(t.docDate)}</Td>
                    <Td>
                      <DocLink onClick={() => handleDocClick(t)}>
                        {t.docNumber || '-'}
                      </DocLink>
                    </Td>
                    <Td>
                      <TranTypeBadge $type={t.tranTypeCode}>
                        {t.tranTypeCode}
                      </TranTypeBadge>
                    </Td>
                    <Td title={t.partyName}>{t.partyName || '-'}</Td>
                    <AmountTd align="right" $highlight>
                      {formatAmountLocal(t.netAmount)}
                    </AmountTd>
                    <Td>{PAYMENT_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || '-'}</Td>
                    <Td align="center">
                      <StatusBadge $status={t.status || 'Draft'}>
                        {t.status || 'Draft'}
                      </StatusBadge>
                    </Td>
                  </Tr>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                    TOTAL ({sortedData.length} records)
                  </Td>
                  <AmountTd align="right" $highlight>{formatAmountLocal(tableTotals.netAmount)}</AmountTd>
                  <Td />
                  <Td />
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {!loading && filteredData.length > 0 && (
        <ChartsGrid>
          {/* Area Chart: Payment Trend */}
          <ChartCard $span={2}>
            <ChartTitle>Payment Trend (Monthly)</ChartTitle>
            {trendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradCash" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#800020" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#800020" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradBank" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#17A2B8" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#17A2B8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: '#DEE2E6' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area
                    type="monotone"
                    dataKey="cash"
                    name="Cash Payments"
                    stroke="#800020"
                    strokeWidth={2}
                    fill="url(#gradCash)"
                  />
                  <Area
                    type="monotone"
                    dataKey="bank"
                    name="Bank Payments"
                    stroke="#17A2B8"
                    strokeWidth={2}
                    fill="url(#gradBank)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No trend data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Pie Chart: Cash vs Bank */}
          <ChartCard>
            <ChartTitle>Cash vs Bank Payments</ChartTitle>
            {paymentModePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={paymentModePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name.length > 15 ? name.substring(0, 15) + '..' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {paymentModePieData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieCustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No payment data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Bar Chart: Top 10 Vendors */}
          <ChartCard>
            <ChartTitle>Top 10 Vendors by Payment Amount</ChartTitle>
            {vendorBarChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={vendorBarChartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="vendor"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    width={130}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" name="Total Paid" fill="#800020" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No vendor data available</ChartEmpty>
            )}
          </ChartCard>
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
//  Styled Components
// ══════════════════════════════════════════════════════════════

// ── Summary Cards ──

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 600;
`;

const CardValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  background: ${colors.neutral[100]};
  color: ${colors.text.primary};
  white-space: nowrap;

  &:hover {
    background: ${colors.neutral[200]};
  }
`;

// ── Charts ──

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  grid-column: ${props => props.$span ? `span ${props.$span}` : 'span 1'};

  @media (max-width: 1024px) {
    grid-column: span 1;
  }
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
`;

const ChartEmpty = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipWrapper = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 10px 14px;
  font-size: 12px;
`;

const ChartTooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
    margin-left: auto;
  }
`;

const ChartTooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const TableHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.sortable && `
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.primary.main}08;
  border-top: 2px solid ${colors.primary.main};

  td {
    font-weight: 700 !important;
    font-size: 13px !important;
    padding: 12px 14px !important;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${props => props.$highlight ? colors.text.primary : colors.text.secondary};
  font-weight: ${props => props.$highlight ? 600 : 400};
`;

const DocLink = styled.span`
  color: ${colors.primary.main};
  font-weight: 600;
  cursor: pointer;
  font-size: 12px;
  font-family: 'SF Mono', 'Consolas', monospace;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.dark};
    text-decoration: underline;
  }
`;

const TranTypeBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${props => props.$type === 'BP' ? `${colors.info.main}15` : `${colors.primary.main}12`};
  color: ${props => props.$type === 'BP' ? colors.info.main : colors.primary.main};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => STATUS_COLORS[props.$status]?.bg || `${colors.neutral[400]}15`};
  color: ${props => STATUS_COLORS[props.$status]?.text || colors.text.secondary};
`;

// ── Empty / Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  margin: 0 auto 16px auto;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default VendorPayments;
